import { useCallback, lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from './state/AuthContext.jsx';
import { usePageRouter, PAGES, PUBLIC_PAGES } from './state/usePageRouter.js';
import { LoginPage } from './pages/LoginPage.jsx';
import { SignupPage } from './pages/SignupPage.jsx';
import { VerifyEmailPage } from './pages/VerifyEmailPage.jsx';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.jsx';
import { ResetPasswordPage } from './pages/ResetPasswordPage.jsx';
import { DashboardPage } from './pages/DashboardPage.jsx';
import { TeamPage } from './pages/TeamPage.jsx';
import { SettingsPage } from './pages/SettingsPage.jsx';
import { AdminPage } from './pages/AdminPage.jsx';
import { AcceptInvitePage } from './pages/AcceptInvitePage.jsx';
import { ReportView } from './pages/ReportView.jsx';
import { SiteFooter } from './components/SiteFooter.jsx';

// Code-split the legal pages: react-markdown + remark-gfm + the verbatim docs
// live in LegalPage's own chunk and load only when /privacy or /terms is
// visited, keeping them out of the main bundle (LEGAL-1 follow-up).
const LegalPage = lazy(() => import('./pages/LegalPage.jsx'));

function LegalFallback() {
  return (
    <div className="legal-page">
      <main className="legal-content" role="main">
        <p>Loading…</p>
      </main>
    </div>
  );
}

/**
 * App — mounts AuthProvider and the router. Protected routes fall through
 * to /login when the session is missing; public auth pages never show the
 * app chrome. Legacy `/?report=<id>` still reaches ReportView without
 * requiring auth, preserving older direct PDF links and existing tests.
 */
export default function App() {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}

function AppRouter() {
  const { route, navigate } = usePageRouter();
  const auth = useAuth();

  const handleLogout = useCallback(async () => {
    await auth.logout();
    navigate('/login');
  }, [auth, navigate]);

  if (PUBLIC_PAGES.has(route.page)) {
    return renderPublicPage(route, navigate, auth);
  }

  if (route.page === PAGES.REPORT && route.isLegacy) {
    return (
      <>
        <ReportView
          reportId={route.reportId || null}
          user={auth.user}
          organization={auth.organization}
          onLogout={auth.status === 'authenticated' ? handleLogout : null}
        />
        <SiteFooter navigate={navigate} />
      </>
    );
  }

  if (auth.status === 'loading') {
    return (
      <div className="fullscreen-spinner" role="status" aria-label="Loading">
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  if (auth.status !== 'authenticated') {
    try {
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath && currentPath !== '/login') {
        sessionStorage.setItem('postLoginRedirect', currentPath);
      }
    } catch { /* sessionStorage unavailable */ }
    return <LoginPage navigate={navigate} />;
  }

  // Authenticated pages share the global SiteFooter (legal links on every page).
  let authedPage;
  if (route.page === PAGES.REPORT) {
    authedPage = (
      <ReportView
        reportId={route.reportId || null}
        user={auth.user}
        organization={auth.organization}
        onLogout={handleLogout}
      />
    );
  } else if (route.page === PAGES.TEAM) {
    authedPage = (
      <TeamPage
        user={auth.user}
        organization={auth.organization}
        onLogout={handleLogout}
        navigate={navigate}
      />
    );
  } else if (route.page === PAGES.SETTINGS) {
    authedPage = (
      <SettingsPage
        user={auth.user}
        organization={auth.organization}
        onLogout={handleLogout}
        navigate={navigate}
        initialSection={route.section}
      />
    );
  } else if (route.page === PAGES.ADMIN && auth.user?.is_owner) {
    // Owner-era: client gate — only the platform owner routes to the oversight
    // page. Non-owners fall through to the dashboard (the API is the hard gate).
    authedPage = (
      <AdminPage
        user={auth.user}
        organization={auth.organization}
        onLogout={handleLogout}
        navigate={navigate}
      />
    );
  } else {
    authedPage = (
      <DashboardPage
        user={auth.user}
        organization={auth.organization}
        onLogout={handleLogout}
        navigate={navigate}
      />
    );
  }

  return (
    <>
      {authedPage}
      <SiteFooter navigate={navigate} />
    </>
  );
}

function renderPublicPage(route, navigate, auth) {
  switch (route.page) {
    case PAGES.LOGIN: return <LoginPage navigate={navigate} />;
    case PAGES.SIGNUP: return <SignupPage navigate={navigate} />;
    case PAGES.VERIFY_EMAIL:
      return <VerifyEmailPage navigate={navigate} emailParam={route.email} />;
    case PAGES.FORGOT_PASSWORD: return <ForgotPasswordPage navigate={navigate} />;
    case PAGES.RESET_PASSWORD:
      return <ResetPasswordPage navigate={navigate} token={route.token} />;
    case PAGES.ACCEPT_INVITE:
      return <AcceptInvitePage navigate={navigate} token={route.token} auth={auth} />;
    // Public legal documents — lazy-loaded behind a Suspense boundary; LegalPage
    // selects + renders the verbatim content (and embeds its own SiteFooter).
    case PAGES.PRIVACY:
      return (
        <Suspense fallback={<LegalFallback />}>
          <LegalPage doc="privacy" navigate={navigate} />
        </Suspense>
      );
    case PAGES.TERMS:
      return (
        <Suspense fallback={<LegalFallback />}>
          <LegalPage doc="terms" navigate={navigate} />
        </Suspense>
      );
    default: return <LoginPage navigate={navigate} />;
  }
}
